import React, { useMemo } from 'react';
import { FileText } from 'lucide-react';
import KYCHeader from './KYCHeader';
import KYCSidebar from './KYCSidebar';
import KYCSectionCard from '../KYCSectionCard';
import { useKYCNavigation } from './hooks/useKYCNavigation';
import { buildDynamicKYCSections, getOverallCompleteness } from '../../../../utils/buildDynamicKYCSections';
import { KYC_SECTION_ICON_MAP } from '../../../../types/kyc.constants';

interface KYCDisplayData {
  status: 'complete' | 'incomplete' | 'in_progress' | 'approved';
  completion_percentage: number;
  last_updated: string;
  sections: Record<string, any>;
  backendStatus?: string;
}

interface KYCReviewLayoutProps {
  kycData: KYCDisplayData;
  clientId: string;
  onEdit: () => void;
  onDownloadPDF: () => void;
}

const iconMap = KYC_SECTION_ICON_MAP;

/**
 * TASK-192: Dynamic KYC Review Layout
 *
 * Shows only sections with extracted data (no more empty sections!)
 * Uses field mapping configuration for type safety and consistency.
 *
 * Story 2.2.9: KYC Review & Navigation
 */
const KYCReviewLayout: React.FC<KYCReviewLayoutProps> = ({
  kycData,
  clientId: _clientId,
  onEdit,
  onDownloadPDF,
}) => {
  const { activeSection, scrollToSection } = useKYCNavigation();

  // TASK-192: Build dynamic sections from extracted data
  // Only sections with data will be included
  const dynamicSections = useMemo(() => {
    return buildDynamicKYCSections(kycData.sections);
  }, [kycData.sections]);

  // TASK-192: Calculate overall completeness from dynamic sections
  const overallCompleteness = useMemo(() => {
    if (dynamicSections.length === 0) return 0;
    return getOverallCompleteness(dynamicSections);
  }, [dynamicSections]);

  // TASK-192: Build completion status for sidebar
  const completionStatus: Record<string, boolean> = useMemo(() => {
    const status: Record<string, boolean> = {};
    dynamicSections.forEach(section => {
      status[section.id] = section.completeness >= 75; // 75%+ considered complete
    });
    return status;
  }, [dynamicSections]);

  return (
    <div className="flex gap-6">
      {/* Sidebar Navigation */}
      <KYCSidebar
        sections={dynamicSections.map(s => ({ id: s.id, title: s.title, icon: s.icon }))}
        activeSection={activeSection}
        onSectionClick={scrollToSection}
        completionStatus={completionStatus}
      />

      {/* Main Content */}
      <div className="flex-1 min-w-0">
        {/* Header with completion and PDF download */}
        <KYCHeader
          completionPercentage={overallCompleteness || kycData.completion_percentage}
          lastUpdated={kycData.last_updated}
          onEdit={onEdit}
          onDownloadPDF={onDownloadPDF}
          backendStatus={kycData.backendStatus}
        />

        {/* TASK-192: Dynamic Section Cards (only show sections with data) */}
        <div className="space-y-4 mt-6">
          {dynamicSections.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <FileText className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p className="text-lg font-medium">No KYC data extracted yet</p>
              <p className="text-sm mt-2">
                Upload documents or complete the KYC form to populate this section.
              </p>
            </div>
          ) : (
            dynamicSections.map((section) => {
              // Get icon component from mapping
              const IconComponent = iconMap[section.icon] || FileText;

              return (
                <KYCSectionCard
                  key={section.id}
                  title={section.title}
                  section={{
                    ...section.fields,
                    complete: section.completeness >= 75,
                    completeness: section.completeness,
                    fieldCount: section.fieldCount,
                    isArray: section.isArray,
                    arrayItems: section.arrayItems,
                  }}
                  icon={<IconComponent className="w-5 h-5" />}
                />
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default KYCReviewLayout;
